/**
 * Card Component tokens (Figma DS AIMS OS — node 5388:23471).
 * Wrapper for Entity List rows and other slotted content.
 */

/** Figma `Size` property on Card Component. */
export const CART_CONTAINER_SIZES = ['S', 'M', 'L'];

/** Figma `Style` property — tinted surfaces (8541:*). Aligned with HighlightCard naming. */
export const CART_CONTAINER_VARIANTS = [
  'neutral',
  'primary',
  'green',
  'orange',
  'yellow',
  'purple',
  'lightBlue',
  'limeGreen',
  'red',
];

/** Figma `State` property: Default | Hover | Selected | Disabled. */
export const CART_CONTAINER_STATES = ['default', 'hover', 'selected', 'disabled'];

export const cartContainerRootClass = 'flex w-full min-w-0 flex-col border-solid';

/** Figma size tokens: S/M → radius-m (8px); L → radius-l (16px). */
export const cartContainerSizeClass = {
  S: {
    padding: 'p-3',
    radius: 'rounded-lg',
  },
  M: {
    padding: 'py-6 px-4',
    radius: 'rounded-lg',
  },
  L: {
    padding: 'p-6',
    radius: 'rounded-2xl',
  },
};

export const cartContainerInteractiveClass = [
  'transition-[box-shadow,border-color,opacity]',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30 focus-visible:ring-offset-2',
].join(' ');


export const cartContainerEmptySlotClass = [
  'flex min-h-[120px] w-full flex-1 flex-col items-center justify-center',
  'rounded-lg border border-dashed border-[#80afff]',
  'text-lg font-semibold text-[#2173ff]',
].join(' ');

const neutralHoverShadow =
  'shadow-[0px_0px_14px_0px_rgba(255,255,255,0.4),0px_0px_4px_0px_rgba(255,255,255,0.9),8px_8px_16px_0px_rgba(0,0,0,0.08)]';

const primaryHoverShadow =
  'shadow-[0px_0px_14px_0px_rgba(33,115,255,0.4),0px_0px_4px_0px_rgba(33,115,255,0.9),8px_8px_16px_0px_rgba(0,0,0,0.08)]';

const greenHoverShadow =
  'shadow-[0px_0px_14px_0px_rgba(0,201,79,0.4),0px_0px_4px_0px_rgba(0,201,79,0.9),8px_8px_16px_0px_rgba(0,0,0,0.08)]';

const redHoverShadow =
  'shadow-[0px_0px_14px_0px_rgba(250,43,54,0.4),0px_0px_4px_0px_rgba(250,43,54,0.9),8px_8px_16px_0px_rgba(0,0,0,0.08)]';

const orangeHoverShadow =
  'shadow-[0px_0px_14px_0px_rgba(237,107,3,0.4),0px_0px_4px_0px_rgba(237,107,3,0.9),8px_8px_16px_0px_rgba(0,0,0,0.08)]';

const yellowHoverShadow =
  'shadow-[0px_0px_14px_0px_rgba(252,199,0,0.4),0px_0px_4px_0px_rgba(252,199,0,0.9),8px_8px_16px_0px_rgba(0,0,0,0.08)]';

const purpleHoverShadow =
  'shadow-[0px_0px_14px_0px_rgba(173,69,255,0.4),0px_0px_4px_0px_rgba(173,69,255,0.9),8px_8px_16px_0px_rgba(0,0,0,0.08)]';

const lightBlueHoverShadow =
  'shadow-[0px_0px_14px_0px_rgba(82,163,255,0.4),0px_0px_4px_0px_rgba(82,163,255,0.9),8px_8px_16px_0px_rgba(0,0,0,0.08)]';

const limeGreenHoverShadow =
  'shadow-[0px_0px_14px_0px_rgba(189,237,74,0.4),0px_0px_4px_0px_rgba(189,237,74,0.9),8px_8px_16px_0px_rgba(0,0,0,0.08)]';

/** Per-variant surfaces (Figma Style → bg + borders per state). */
export const cartContainerVariantSurface = {
  neutral: {
    default: [
      'bg-white dark:bg-surface',
      'border-[0.5px] border-[#bababa] dark:border-edge',
    ].join(' '),
    hover: [
      'bg-white dark:bg-surface',
      'border-[0.5px] border-[#5c5c5c] dark:border-edge-strong',
      neutralHoverShadow,
    ].join(' '),
    hoverInteractive: [
      'hover:border-[#5c5c5c] dark:hover:border-edge-strong',
      'hover:shadow-[0px_0px_14px_0px_rgba(255,255,255,0.4),0px_0px_4px_0px_rgba(255,255,255,0.9),8px_8px_16px_0px_rgba(0,0,0,0.08)]',
    ].join(' '),
    selected: [
      'bg-white dark:bg-surface',
      'border border-[#2173ff] dark:border-blue-500',
    ].join(' '),
    disabled: [
      'bg-white dark:bg-surface',
      'border-[0.5px] border-[#5c5c5c] dark:border-edge',
      'opacity-50',
    ].join(' '),
  },
  primary: {
    default: [
      'bg-[#f6f9ff] dark:bg-blue-950/35',
      'border-[0.5px] border-[#80afff] dark:border-blue-800/60',
    ].join(' '),
    hover: [
      'bg-[#f6f9ff] dark:bg-blue-950/35',
      'border-[0.5px] border-[#2173ff] dark:border-blue-500',
      primaryHoverShadow,
    ].join(' '),
    hoverInteractive: [
      'hover:border-[#2173ff] dark:hover:border-blue-500',
      'hover:shadow-[0px_0px_14px_0px_rgba(33,115,255,0.4),0px_0px_4px_0px_rgba(33,115,255,0.9),8px_8px_16px_0px_rgba(0,0,0,0.08)]',
    ].join(' '),
    selected: [
      'bg-[#f6f9ff] dark:bg-blue-950/35',
      'border border-[#2173ff] dark:border-blue-500',
    ].join(' '),
    disabled: [
      'bg-[#f6f9ff] dark:bg-blue-950/35',
      'border-[0.5px] border-[#80afff] dark:border-blue-800/40',
      'opacity-50',
    ].join(' '),
  },
  green: {
    default: [
      'bg-[#e5fdf8] dark:bg-emerald-950/35',
      'border-[0.5px] border-[#00a07e] dark:border-emerald-800/60',
    ].join(' '),
    hover: [
      'bg-[#e5fdf8] dark:bg-emerald-950/35',
      'border-[0.5px] border-[#009978] dark:border-emerald-600',
      greenHoverShadow,
    ].join(' '),
    hoverInteractive: [
      'hover:border-[#009978] dark:hover:border-emerald-600',
      'hover:shadow-[0px_0px_14px_0px_rgba(0,201,79,0.4),0px_0px_4px_0px_rgba(0,201,79,0.9),8px_8px_16px_0px_rgba(0,0,0,0.08)]',
    ].join(' '),
    selected: [
      'bg-[#e5fdf8] dark:bg-emerald-950/35',
      'border border-[#2173ff] dark:border-blue-500',
    ].join(' '),
    disabled: [
      'bg-[#e5fdf8] dark:bg-emerald-950/35',
      'border-[0.5px] border-[#00a07e] dark:border-emerald-800/40',
      'opacity-50',
    ].join(' '),
  },
  orange: {
    default: [
      'bg-[#fff4e5] dark:bg-amber-950/35',
      'border-[0.5px] border-[#edc6a6] dark:border-amber-700/60',
    ].join(' '),
    hover: [
      'bg-[#fff4e5] dark:bg-amber-950/35',
      'border-[0.5px] border-[#b25102] dark:border-amber-600',
      orangeHoverShadow,
    ].join(' '),
    hoverInteractive: [
      'hover:border-[#b25102] dark:hover:border-amber-600',
      'hover:shadow-[0px_0px_14px_0px_rgba(237,107,3,0.4),0px_0px_4px_0px_rgba(237,107,3,0.9),8px_8px_16px_0px_rgba(0,0,0,0.08)]',
    ].join(' '),
    selected: [
      'bg-[#fff4e5] dark:bg-amber-950/35',
      'border border-[#2173ff] dark:border-blue-500',
    ].join(' '),
    disabled: [
      'bg-[#fff4e5] dark:bg-amber-950/35',
      'border-[0.5px] border-[#edc6a6] dark:border-amber-700/40',
      'opacity-50',
    ].join(' '),
  },
  yellow: {
    default: [
      'bg-[#fffaf0] dark:bg-amber-950/30',
      'border-[0.5px] border-[#edc6a6] dark:border-amber-600/60',
    ].join(' '),
    hover: [
      'bg-[#fffaf0] dark:bg-amber-950/30',
      'border-[0.5px] border-[#ed6c02] dark:border-amber-500',
      yellowHoverShadow,
    ].join(' '),
    hoverInteractive: [
      'hover:border-[#ed6c02] dark:hover:border-amber-500',
      'hover:shadow-[0px_0px_14px_0px_rgba(252,199,0,0.4),0px_0px_4px_0px_rgba(252,199,0,0.9),8px_8px_16px_0px_rgba(0,0,0,0.08)]',
    ].join(' '),
    selected: [
      'bg-[#fffaf0] dark:bg-amber-950/30',
      'border border-[#2173ff] dark:border-blue-500',
    ].join(' '),
    disabled: [
      'bg-[#fffaf0] dark:bg-amber-950/30',
      'border-[0.5px] border-[#edc6a6] dark:border-amber-600/40',
      'opacity-50',
    ].join(' '),
  },
  purple: {
    default: [
      'bg-[#f3e9fd] dark:bg-purple-950/35',
      'border-[0.5px] border-[#cfa7f9] dark:border-purple-700/60',
    ].join(' '),
    hover: [
      'bg-[#f3e9fd] dark:bg-purple-950/35',
      'border-[0.5px] border-[#7b27ed] dark:border-purple-500',
      purpleHoverShadow,
    ].join(' '),
    hoverInteractive: [
      'hover:border-[#7b27ed] dark:hover:border-purple-500',
      'hover:shadow-[0px_0px_14px_0px_rgba(173,69,255,0.4),0px_0px_4px_0px_rgba(173,69,255,0.9),8px_8px_16px_0px_rgba(0,0,0,0.08)]',
    ].join(' '),
    selected: [
      'bg-[#f3e9fd] dark:bg-purple-950/35',
      'border border-[#2173ff] dark:border-blue-500',
    ].join(' '),
    disabled: [
      'bg-[#f3e9fd] dark:bg-purple-950/35',
      'border-[0.5px] border-[#cfa7f9] dark:border-purple-700/40',
      'opacity-50',
    ].join(' '),
  },
  lightBlue: {
    default: [
      'bg-[#e5f8ff] dark:bg-sky-950/35',
      'border-[0.5px] border-[#99e5f9] dark:border-sky-700/60',
    ].join(' '),
    hover: [
      'bg-[#e5f8ff] dark:bg-sky-950/35',
      'border-[0.5px] border-[#00b5d9] dark:border-sky-500',
      lightBlueHoverShadow,
    ].join(' '),
    hoverInteractive: [
      'hover:border-[#00b5d9] dark:hover:border-sky-500',
      'hover:shadow-[0px_0px_14px_0px_rgba(82,163,255,0.4),0px_0px_4px_0px_rgba(82,163,255,0.9),8px_8px_16px_0px_rgba(0,0,0,0.08)]',
    ].join(' '),
    selected: [
      'bg-[#e5f8ff] dark:bg-sky-950/35',
      'border border-[#2173ff] dark:border-blue-500',
    ].join(' '),
    disabled: [
      'bg-[#e5f8ff] dark:bg-sky-950/35',
      'border-[0.5px] border-[#99e5f9] dark:border-sky-700/40',
      'opacity-50',
    ].join(' '),
  },
  limeGreen: {
    default: [
      'bg-[#f9fee5] dark:bg-lime-950/30',
      'border-[0.5px] border-[#d4f381] dark:border-lime-600/60',
    ].join(' '),
    hover: [
      'bg-[#f9fee5] dark:bg-lime-950/30',
      'border-[0.5px] border-[#a0da1d] dark:border-lime-500',
      limeGreenHoverShadow,
    ].join(' '),
    hoverInteractive: [
      'hover:border-[#a0da1d] dark:hover:border-lime-500',
      'hover:shadow-[0px_0px_14px_0px_rgba(189,237,74,0.4),0px_0px_4px_0px_rgba(189,237,74,0.9),8px_8px_16px_0px_rgba(0,0,0,0.08)]',
    ].join(' '),
    selected: [
      'bg-[#f9fee5] dark:bg-lime-950/30',
      'border border-[#2173ff] dark:border-blue-500',
    ].join(' '),
    disabled: [
      'bg-[#f9fee5] dark:bg-lime-950/30',
      'border-[0.5px] border-[#d4f381] dark:border-lime-600/40',
      'opacity-50',
    ].join(' '),
  },
  red: {
    default: [
      'bg-[#fdeded] dark:bg-red-950/35',
      'border-[0.5px] border-[#d32f2f] dark:border-red-800/60',
    ].join(' '),
    hover: [
      'bg-[#fdeded] dark:bg-red-950/35',
      'border-[0.5px] border-[#d32f2f] dark:border-red-500',
      redHoverShadow,
    ].join(' '),
    hoverInteractive: [
      'hover:border-[#d32f2f] dark:hover:border-red-500',
      'hover:shadow-[0px_0px_14px_0px_rgba(250,43,54,0.4),0px_0px_4px_0px_rgba(250,43,54,0.9),8px_8px_16px_0px_rgba(0,0,0,0.08)]',
    ].join(' '),
    selected: [
      'bg-[#fdeded] dark:bg-red-950/35',
      'border border-[#2173ff] dark:border-blue-500',
    ].join(' '),
    disabled: [
      'bg-[#fdeded] dark:bg-red-950/35',
      'border-[0.5px] border-[#d32f2f] dark:border-red-800/40',
      'opacity-50',
    ].join(' '),
  },
};

export function normalizeCartContainerSize(size) {
  return CART_CONTAINER_SIZES.includes(size) ? size : 'L';
}

export function normalizeCartContainerVariant(variant) {
  return CART_CONTAINER_VARIANTS.includes(variant) ? variant : 'neutral';
}

/** Explicit `state` (except default) wins; otherwise `disabled` / `selected` booleans. */
export function normalizeCartContainerState(state, { selected, disabled } = {}) {
  if (CART_CONTAINER_STATES.includes(state) && state !== 'default') {
    return state;
  }
  if (disabled === true) return 'disabled';
  if (selected === true) return 'selected';
  return 'default';
}

/**
 * Resolves container surface classes for variant + state.
 * When `state` is `default` and `interactive`, includes `hover:` pseudo-classes.
 */
export function resolveCartContainerSurface(variant, state, { interactive = false } = {}) {
  const v = cartContainerVariantSurface[variant] ?? cartContainerVariantSurface.neutral;

  if (state === 'hover') return v.hover;
  if (state === 'selected') return v.selected;
  if (state === 'disabled') return v.disabled;

  if (interactive) {
    return [v.default, v.hoverInteractive].join(' ');
  }

  return v.default;
}
